import { kpLiveApi } from '@/services/kursi-play-v2/kpLiveApiClient';
import { loadPracticeCenterData } from '@/features/unified-learning/runtime/practiceCenterService';
import type { KpQualityTier, KpScenarioRuntime, KpV2LessonWithStatus } from '@/types/kursi-play-v2';

export type ExamQueueItem = {
  lessonId: string;
  lessonTitle: string;
  scenario: KpScenarioRuntime;
};

export type ExamReadinessBand = 'ready' | 'almost_ready' | 'needs_practice';

export type ExamSummary = {
  totalQuestions: number;
  answeredQuestions: number;
  xpEarned: number;
  qualityCounts: Record<KpQualityTier, number>;
  strongLessonIds: string[];
  weakLessonIds: string[];
  readinessBand: ExamReadinessBand;
  readinessLabel: string;
  readinessGuidance: string;
};

const QUALITY_POINTS: Record<KpQualityTier, number> = {
  strong: 4,
  acceptable: 3,
  weak: 2,
  risky: 1,
};

function rankLessonsForExam(lessons: KpV2LessonWithStatus[], weakLessonIds: string[]): string[] {
  const unlocked = lessons
    .filter((l) => l.status !== 'locked')
    .sort((a, b) => a.order_index - b.order_index)
    .map((l) => l.id);

  const weakFirst = weakLessonIds.filter((id) => unlocked.includes(id));
  const rest = unlocked.filter((id) => !weakFirst.includes(id));
  return [...weakFirst, ...rest];
}

export async function buildExamQueue(slug: string, userId: string, targetQuestions = 10): Promise<{ queue: ExamQueueItem[]; lessonPool: string[] }> {
  const center = await loadPracticeCenterData(slug, userId);
  const lessonPool = rankLessonsForExam(center.lessons, center.weakLessonIds);
  if (lessonPool.length === 0) return { queue: [], lessonPool: [] };

  const runtimes = await Promise.all(
    lessonPool.map((lessonId) => kpLiveApi.getLessonRuntime(lessonId, slug, userId)),
  );

  const allItems: ExamQueueItem[] = [];
  for (const rt of runtimes) {
    for (const scenario of rt.scenarios) {
      allItems.push({
        lessonId: rt.lesson.id,
        lessonTitle: rt.lesson.title,
        scenario,
      });
    }
  }

  return {
    queue: allItems.slice(0, Math.max(1, targetQuestions)),
    lessonPool,
  };
}

export function summarizeExamSession(input: {
  totalQuestions: number;
  answeredQuestions: number;
  xpEarned: number;
  qualityCounts: Record<KpQualityTier, number>;
  lessonScoreMap: Record<string, { attempts: number; score: number }>;
}): ExamSummary {
  const { totalQuestions, answeredQuestions, xpEarned, qualityCounts, lessonScoreMap } = input;
  const entries = Object.entries(lessonScoreMap)
    .map(([lessonId, st]) => ({ lessonId, avg: st.score / Math.max(st.attempts, 1), attempts: st.attempts }))
    .sort((a, b) => a.avg - b.avg);

  const weakLessonIds = entries.filter((e) => e.attempts > 0 && e.avg <= 2.5).slice(0, 4).map((e) => e.lessonId);
  const strongLessonIds = [...entries].reverse().filter((e) => e.attempts > 0 && e.avg >= 3.5).slice(0, 4).map((e) => e.lessonId);

  const answered = Math.max(answeredQuestions, 1);
  const avg = (
    (qualityCounts.strong * QUALITY_POINTS.strong) +
    (qualityCounts.acceptable * QUALITY_POINTS.acceptable) +
    (qualityCounts.weak * QUALITY_POINTS.weak) +
    (qualityCounts.risky * QUALITY_POINTS.risky)
  ) / answered;

  let readinessBand: ExamReadinessBand = 'needs_practice';
  let readinessLabel = 'Needs more practice';
  let readinessGuidance = 'Return to focused weakness practice, then run another Exam Mode session.';

  if (avg >= 3.4 && qualityCounts.risky <= 1) {
    readinessBand = 'ready';
    readinessLabel = 'Ready to advance';
    readinessGuidance = 'You are on track. Complete a new lesson or run another Exam Mode session to validate consistency.';
  } else if (avg >= 2.8) {
    readinessBand = 'almost_ready';
    readinessLabel = 'Almost ready';
    readinessGuidance = 'Strong foundation so far, but there are still weak zones that need reinforcement.';
  }

  return {
    totalQuestions,
    answeredQuestions,
    xpEarned,
    qualityCounts,
    strongLessonIds,
    weakLessonIds,
    readinessBand,
    readinessLabel,
    readinessGuidance,
  };
}

export function qualityCountIncrement(base: Record<KpQualityTier, number>, tier: KpQualityTier): Record<KpQualityTier, number> {
  return {
    ...base,
    [tier]: (base[tier] ?? 0) + 1,
  };
}

export function qualityToPoints(tier: KpQualityTier): number {
  return QUALITY_POINTS[tier] ?? 2;
}
